/**
 * Damped additive Holt-Winters with weekly seasonality (m = 7).
 *
 * The seasonal state is indexed by DAY-OF-WEEK rather than `t mod 7`, so a
 * gap in the series cannot rotate the seasonal pattern out of alignment.
 * (Level/trend updates still treat consecutive observations as one step
 * apart; long gaps mildly distort the trend — acceptable for daily
 * restaurant series, documented here.)
 *
 * α/β/γ are fit by a coarse, fully deterministic grid search
 * ({0.05 … 0.5 step 0.05} each) minimizing one-step-ahead SSE; ties keep the
 * first (lowest-index) combination. φ (damping) defaults to 0.98.
 *
 * Recurrences (Hyndman & Athanasopoulos, damped additive):
 *   forecast:  ŷ_t = ℓ + φb + s[dow_t]
 *   level:     ℓ' = α(y_t − s[dow_t]) + (1 − α)(ℓ + φb)
 *   trend:     b' = β(ℓ' − ℓ) + (1 − β)φb
 *   seasonal:  s[dow_t] ← γ(y_t − ℓ − φb) + (1 − γ)s[dow_t]
 *   h-step:    ŷ_{T+h} = ℓ_T + (φ + φ² + … + φ^h)b_T + s[dow_{T+h}]
 */

import { diffDays } from './dates';
import { intervalFor, residualQuantilesByDow } from './intervals';
import type {
    DailyObservation,
    DowResidual,
    FitResult,
    ForecastInput,
    ForecastPoint,
    HoltWintersModel,
    ModelFactory,
} from './types';

export const HW_DEFAULT_PHI = 0.98;

/** {0.05, 0.10, …, 0.50} — built multiplicatively to avoid FP drift. */
export const HW_DEFAULT_GRID: readonly number[] = Array.from(
    { length: 10 },
    (_, i) => (i + 1) * 0.05,
);

export interface HoltWintersOptions {
    /** Damping factor φ ∈ (0, 1]. Default {@link HW_DEFAULT_PHI}. */
    phi?: number;
    /** Candidate values for each of α, β, γ. Default {@link HW_DEFAULT_GRID}. */
    grid?: readonly number[];
}

const SEASON_LENGTH = 7;
/** Observations used to initialize level/trend/seasonal state. */
const INIT_SPAN = 2 * SEASON_LENGTH;
const MIN_OBSERVATIONS = 3 * SEASON_LENGTH;

interface PassResult {
    sse: number;
    level: number;
    trend: number;
    seasonal: number[];
    residuals: DowResidual[];
}

function mean(values: number[]): number {
    let sum = 0;
    for (const v of values) {
        sum += v;
    }
    return values.length > 0 ? sum / values.length : 0;
}

function initialState(sorted: DailyObservation[]): {
    level: number;
    trend: number;
    seasonal: number[];
} {
    const week1 = sorted.slice(0, SEASON_LENGTH).map((o) => o.value);
    const week2 = sorted.slice(SEASON_LENGTH, INIT_SPAN).map((o) => o.value);
    const level = mean(week1);
    const trend = (mean(week2) - mean(week1)) / SEASON_LENGTH;

    const seasonal = new Array<number>(SEASON_LENGTH).fill(0);
    const counts = new Array<number>(SEASON_LENGTH).fill(0);
    const initLevel = mean([...week1, ...week2]);
    for (let i = 0; i < INIT_SPAN && i < sorted.length; i++) {
        const obs = sorted[i];
        seasonal[obs.dayOfWeek] += obs.value - initLevel;
        counts[obs.dayOfWeek]++;
    }
    for (let d = 0; d < SEASON_LENGTH; d++) {
        seasonal[d] = counts[d] > 0 ? seasonal[d] / counts[d] : 0;
    }
    return { level, trend, seasonal };
}

function runPass(
    sorted: DailyObservation[],
    alpha: number,
    beta: number,
    gamma: number,
    phi: number,
): PassResult {
    const init = initialState(sorted);
    let level = init.level;
    let trend = init.trend;
    const seasonal = [...init.seasonal];

    let sse = 0;
    const residuals: DowResidual[] = [];
    for (let i = INIT_SPAN; i < sorted.length; i++) {
        const obs = sorted[i];
        const dampedTrend = phi * trend;
        const forecast = level + dampedTrend + seasonal[obs.dayOfWeek];
        const error = obs.value - forecast;
        sse += error * error;
        residuals.push({ dayOfWeek: obs.dayOfWeek, residual: error });

        const newLevel =
            alpha * (obs.value - seasonal[obs.dayOfWeek]) + (1 - alpha) * (level + dampedTrend);
        const newTrend = beta * (newLevel - level) + (1 - beta) * dampedTrend;
        seasonal[obs.dayOfWeek] =
            gamma * (obs.value - level - dampedTrend) + (1 - gamma) * seasonal[obs.dayOfWeek];
        level = newLevel;
        trend = newTrend;
    }
    return { sse, level, trend, seasonal, residuals };
}

/**
 * Fit damped additive Holt-Winters. Requires at least three weeks of data
 * (two to initialize state, one to smooth against).
 */
export function fitHoltWinters(
    observations: DailyObservation[],
    options: HoltWintersOptions = {},
): FitResult<HoltWintersModel> {
    if (observations.length < MIN_OBSERVATIONS) {
        throw new Error(
            `fitHoltWinters: at least ${MIN_OBSERVATIONS} observations are required, got ${observations.length}`,
        );
    }
    const phi = options.phi ?? HW_DEFAULT_PHI;
    if (phi <= 0 || phi > 1) {
        throw new Error(`fitHoltWinters: phi must be in (0, 1], got ${phi}`);
    }
    const grid = options.grid ?? HW_DEFAULT_GRID;
    const sorted = [...observations].sort((a, b) => (a.date < b.date ? -1 : 1));

    // Deterministic grid search: ascending α → β → γ, strict `<` keeps the
    // first minimum on ties.
    let best: PassResult | null = null;
    let bestParams: [number, number, number] = [grid[0], grid[0], grid[0]];
    for (const alpha of grid) {
        for (const beta of grid) {
            for (const gamma of grid) {
                const pass = runPass(sorted, alpha, beta, gamma, phi);
                if (best === null || pass.sse < best.sse) {
                    best = pass;
                    bestParams = [alpha, beta, gamma];
                }
            }
        }
    }
    // `best` is always set: the grid is non-empty and runPass always returns.
    const fitted = best as PassResult;
    const [alpha, beta, gamma] = bestParams;
    const lastDate = sorted[sorted.length - 1].date;

    const model: HoltWintersModel = {
        type: 'holt_winters',
        alpha,
        beta,
        gamma,
        phi,
        level: fitted.level,
        trend: fitted.trend,
        seasonalByDow: fitted.seasonal,
        lastDate,
    };
    const quantiles = residualQuantilesByDow(fitted.residuals);

    const predict = (days: ForecastInput[]): ForecastPoint[] => {
        const ordered = [...days].sort((a, b) => (a.date < b.date ? -1 : 1));
        return ordered.map((day) => {
            const h = Math.max(1, diffDays(day.date, model.lastDate));
            // Σ_{j=1..h} φ^j
            let dampedSum = 0;
            let phiPow = 1;
            for (let j = 1; j <= h; j++) {
                phiPow *= model.phi;
                dampedSum += phiPow;
            }
            const yhat = Math.max(
                0,
                model.level + dampedSum * model.trend + model.seasonalByDow[day.dayOfWeek],
            );
            const { p10, p90 } = intervalFor(yhat, day.dayOfWeek, quantiles);
            return { date: day.date, yhat, p10, p90 };
        });
    };

    return { model, residuals: fitted.residuals, predict };
}

/** Backtest factory for the Holt-Winters forecaster. */
export function holtWintersFactory(options: HoltWintersOptions = {}): ModelFactory {
    return {
        name: 'holt_winters',
        fit: (train) => fitHoltWinters(train, options),
    };
}
